import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ShieldAlert, ExternalLink, AlertTriangle, Package } from "lucide-react";
import { AppVulnerability } from "@/types/appVulnerability";
import { SeverityBadge } from "../shared/SeverityBadge";
import { getSeverityProgress, getSeverityColorValue } from "../shared/utils";
import { Badge } from "@/components/ui/badge";

interface AppVulnerabilityTableProps {
  vulnerabilities: AppVulnerability[];
  onRowClick: (vulnerability: AppVulnerability) => void;
}

export function AppVulnerabilityTable({ vulnerabilities, onRowClick }: AppVulnerabilityTableProps) {
  if (vulnerabilities.length === 0) {
    return (
      <div className="text-center py-12 backdrop-blur-md bg-white/40 dark:bg-slate-900/40 border border-white/10 dark:border-white/5 rounded-2xl shadow-lg">
        <ShieldAlert className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <p className="text-muted-foreground">No application vulnerabilities found matching your criteria.</p>
      </div>
    );
  }

  return (
    <div className="backdrop-blur-md bg-white/40 dark:bg-slate-900/40 border border-white/10 dark:border-white/5 rounded-2xl shadow-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>CVE ID</TableHead>
            <TableHead>Application</TableHead>
            <TableHead>Severity</TableHead>
            <TableHead className="text-right">CVSS Score</TableHead>
            <TableHead className="text-center">Exploited</TableHead>
            <TableHead className="text-right">Devices</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {vulnerabilities.map((vuln) => (
            <TableRow
              key={vuln.id}
              className="cursor-pointer hover:bg-white/60 dark:hover:bg-slate-800/60 hover:backdrop-blur-lg transition-all duration-200"
              onClick={() => onRowClick(vuln)}
            >
              <TableCell>
                <a
                  href={`https://nvd.nist.gov/vuln/detail/${vuln.cve_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 font-mono text-sm hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ShieldAlert className="h-4 w-4 text-muted-foreground" />
                  {vuln.cve_id}
                  <ExternalLink className="h-3 w-3" />
                </a>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-3">
                  <div className="flex items-center -space-x-2">
                    {vuln.affected_apps.slice(0, 3).map((app, idx) => {
                      const hasIcon = app.icon_url && (app.icon_url.startsWith('data:') || app.icon_url.startsWith('http'));
                      return (
                        <div
                          key={idx}
                          className="flex items-center justify-center w-8 h-8 rounded-full bg-background border-2 border-background"
                          title={`${app.app_name} v${app.app_version}`}
                        >
                          {hasIcon ? (
                            <img 
                              src={app.icon_url!} 
                              alt={app.app_name}
                              className="w-6 h-6 rounded-sm object-cover"
                            />
                          ) : (
                            <Package className="h-4 w-4 text-muted-foreground" />
                          )}
                        </div>
                      );
                    })}
                    {vuln.affected_apps.length > 3 && (
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-muted border-2 border-background">
                        <span className="text-xs font-medium">+{vuln.affected_apps.length - 3}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col">
                    <span className="font-medium">
                      {vuln.affected_apps.length === 1
                        ? vuln.affected_apps[0].app_name
                        : `${vuln.affected_apps.length} Applications`}
                    </span>
                    {vuln.affected_apps.length === 1 && (
                      <span className="text-xs text-muted-foreground">
                        v{vuln.affected_apps[0].app_version}
                      </span>
                    )}
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <div className="space-y-2">
                  <SeverityBadge severity={vuln.severity} />
                  <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full transition-all"
                      style={{
                        width: `${getSeverityProgress(vuln.severity)}%`,
                        backgroundColor: getSeverityColorValue(vuln.severity)
                      }}
                    />
                  </div>
                </div>
              </TableCell>
              <TableCell className="text-right">
                <span className="font-semibold">{vuln.cvss_score.toFixed(1)}</span>
                <span className="text-xs text-muted-foreground ml-1">/10</span>
              </TableCell>
              <TableCell className="text-center">
                {vuln.actively_exploited ? (
                  <div className="flex items-center justify-center gap-1">
                    <AlertTriangle className="h-4 w-4 text-[hsl(30_80%_55%)]" />
                    <span className="text-sm font-semibold text-[hsl(30_80%_55%)]">Yes</span>
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">No</span>
                )}
              </TableCell>
              <TableCell className="text-right">
                <span className="font-semibold">{vuln.affected_devices_count}</span>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
